import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiBi, { creances as api } from '../services/api';
import { useBi } from '../BiContext';
import FiltresPanel from './FiltresPanel';
import { basculer, usePanneau } from './ventesUtils';
import { SelecteurMaille, useRegroupement, construireArbre, axesDisponibles } from './regroupements';
import './Stock.css';   // st-tools / st-seg des réglages d'affichage
import './Creances.css';

// ─── Créances clients — balance âgée (aged receivables) ──────────────────────
// Une PHOTO à date, pas une série mensuelle : encours à recouvrer par société,
// ventilé selon l'ancienneté de l'échéance. La sélection de sociétés vit dans le
// panneau de filtres (comme les autres rapports BI) : KPI et barre d'ancienneté
// portent sur l'agrégat coché ; le détail par client (parcours, export,
// échéances) n'a de sens que pour UNE société — proposé quand une seule est
// cochée. Sa liste est complète et paginée par 20.
//
// `branche` choisit le PÉRIMÈTRE lu dans l'entrepôt : 'auto-antilles' (les 5
// sociétés du fichier racine) ou 'groupe' (les 27 sociétés consolidées du
// dossier /GROUPE). L'écran est le même, seules les tables sources changent.

// La palette d'ancienneté a disparu avec le graphique de répartition
// (2026-09-20) : plus aucune couleur ne code une tranche dans ce rapport.
// L'ancienneté se lit dans l'ordre des colonnes, et les montants en chiffres.
const COULEURS = ['#2E5BFF', '#C9A84C', '#0F766E', '#9D174D', '#4D7C0F', '#B45309', '#475569'];
const nf = new Intl.NumberFormat('fr-FR');
const eur = (v) => nf.format(Math.round(v || 0)) + ' €';
const cM = (v) => {
  v = Math.round(v || 0);
  const a = Math.abs(v);
  if (a >= 1e6) return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(v / 1e6) + ' M€';
  if (a >= 1e3) return nf.format(Math.round(v / 1e3)) + ' k€';
  return v + ' €';
};
const kc = (v) => (!v ? '·' : nf.format(Math.round(v / 1000)) + ' k');
// Le détail par client se lit tantôt en k€ (comparer des lignes), tantôt à
// l'euro (rapprocher une facture). Un seul sélecteur, un seul formateur.
const mont = (v, u) => (!v ? '·' : u === 'e' ? nf.format(Math.round(v)) : kc(v));
const pc1 = (a, b) => (!b ? '·' : nf.format(Math.round((a / b) * 1000) / 10) + ' %');
const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);
const somme = (a) => a.reduce((s, x) => s + x, 0);

export default function Creances({ branche = 'auto-antilles' }) {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  // La liste complète est chargée dès qu'une société est choisie : le bouton
  // « Voir tous les clients » obligeait à un geste pour obtenir ce que l'écran
  // devrait montrer d'emblée, et faisait cohabiter deux jeux de lignes (le top
  // 8 du résumé et la liste complète) dont les pieds ne disaient pas la même
  // chose. Tout est chargé, on en montre 20 et on tourne les pages.
  const [listeComplete, setListeComplete] = useState(null);
  const [page, setPage] = useState(0);
  const [q, setQ] = useState('');
  const [drill, setDrill] = useState(null);          // { tiers, nom, echeances }
  const [uniteSoc, setUniteSoc] = useState('montants'); // montants | clients | part
  const [trancheCli, setTrancheCli] = useState(-1);     // -1 = toutes les tranches
  const [uniteCli, setUniteCli] = useState('k');        // k | e — lecture du détail client
  // Le panneau commun : replié d'office sur téléphone (ventesUtils.js).
  const [panneauOuvert, togglePanneau] = usePanneau();

  useEffect(() => {
    let actif = true;
    setData(null);
    setErreur('');
    api.resume(branche).then((r) => { if (actif) setData(r.data); }).catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Erreur de chargement'); });
    return () => { actif = false; };
  }, [branche]);

  const labels = useMemo(() => data?.labels || [], [data]);
  const societes = useMemo(() => data?.societes || [], [data]);
  const couleurDe = useMemo(() => { const m = {}; societes.forEach((s, i) => { m[s.code] = COULEURS[i % COULEURS.length]; }); return m; }, [societes]);

  // Sélection depuis l'URL : vide = toutes, '__aucune__' = aucune.
  const societesUrl = params.get('societes') || '';
  const selected = useMemo(() => {
    if (societesUrl === '__aucune__') return [];
    if (!societesUrl) return societes.map((s) => s.code);
    return societesUrl.split(',').filter(Boolean);
  }, [societesUrl, societes]);
  const selSet = new Set(selected);
  const selUnique = selected.length === 1 ? selected[0] : null;

  // Le détail par client se réinitialise quand la société ciblée change.
  useEffect(() => { setListeComplete(null); setQ(''); setDrill(null); setPage(0); }, [selUnique]);

  const setFiltre = (v) => { const next = new URLSearchParams(params); if (v) next.set('societes', v); else next.delete('societes'); setParams(next, { replace: true }); };

  // ─── Clients : hors groupe / intra-groupe (2026-10-05) ─────────────────────
  // Filtre du panneau, porté par l'URL (`clients`) : vide = les deux,
  // `externes`, `groupe`, ou `__aucun__`. Le serveur renvoie pour chaque société
  // les deux parts (`ext`, `grp`, disjointes) : on choisit ici, sans recharger.
  // Intra-groupe = code tiers « 3 chiffres + nom » (services/creances.js).
  const clientsUrl = params.get('clients') || '';
  const clientsActifs = clientsUrl === '__aucun__' ? [] : clientsUrl ? [clientsUrl] : ['externes', 'groupe'];
  const setClients = (v) => { const next = new URLSearchParams(params); if (v) next.set('clients', v); else next.delete('clients'); setParams(next, { replace: true }); };
  const toggleClients = (code) => {
    const l = clientsActifs.includes(code) ? clientsActifs.filter((c) => c !== code) : [...clientsActifs, code];
    setClients(l.length === 2 ? '' : l.length === 0 ? '__aucun__' : l[0]);
  };
  const partVide = { buckets: [0, 0, 0, 0, 0, 0, 0], clientsBuckets: [0, 0, 0, 0, 0, 0, 0], clients: 0, total: 0 };
  const partDe = (s) => (clientsUrl === 'externes' ? s.ext : clientsUrl === 'groupe' ? s.grp : clientsUrl === '__aucun__' ? partVide : null) || {};
  const toggleSociete = (code) => {
    const suivantes = selSet.has(code) ? selected.filter((c) => c !== code) : [...selected, code];
    setFiltre(suivantes.length === 0 ? '__aucune__' : suivantes.length === societes.length ? '' : suivantes.join(','));
  };

  // Agrégat des sociétés cochées.
  const selBrut = societes.filter((s) => selSet.has(s.code));
  // La part retenue par le filtre Clients remplace les totaux de chaque société.
  const sel = selBrut.map((s) => ({ ...s, ...partDe(s) }));
  const courant = useMemo(() => {
    const buckets = labels.map((_, i) => sel.reduce((s, x) => s + x.buckets[i], 0));
    // ⚠ Sommer les nombres de clients par tranche entre sociétés surcompte un
    // client présent chez deux d'entre elles. C'est assumé : la ligne « Total »
    // du tableau des concessions est une somme de concessions, pas un
    // dédoublonnage groupe — et le total des clients, lui, reste celui du
    // serveur.
    const clientsBuckets = labels.map((_, i) => sel.reduce((s, x) => s + ((x.clientsBuckets || [])[i] || 0), 0));
    const clients = sel.reduce((s, x) => s + x.clients, 0);
    const nom = selUnique ? (sel[0]?.nom || '') : selected.length === societes.length ? 'Toutes les sociétés' : `${selected.length} sociétés`;
    return { buckets, clientsBuckets, clients, nom };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [societesUrl, societes, labels, clientsUrl]);

  // ─── Maille d'affichage du bloc par concession (§4 bis) ────────────────────
  // ⚠ Seules les mesures ADDITIVES se somment. Le nombre de clients d'un
  // regroupement reste une SOMME de concessions, pas un dédoublonnage : un
  // client servi par deux sociétés d'un même site y compte deux fois. C'est
  // assumé, et c'est déjà vrai de la ligne « Total » du tableau.
  const feuilles = useMemo(
    () => sel.map((x) => ({ ...x, libelle: x.nom })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [societesUrl, societes, clientsUrl]);
  const axes = useMemo(() => axesDisponibles(feuilles), [feuilles]);
  const sommerSoc = (membres) => {
    const buckets = labels.map((_, i) => membres.reduce((t, m) => t + (m.buckets[i] || 0), 0));
    const clientsBuckets = labels.map((_, i) => membres.reduce((t, m) => t + ((m.clientsBuckets || [])[i] || 0), 0));
    return { buckets, clientsBuckets,
      clients: membres.reduce((t, m) => t + (m.clients || 0), 0),
      total: buckets.reduce((a, b) => a + b, 0) };
  };
  const maille = useRegroupement();
  const arbre = useMemo(
    () => construireArbre(feuilles, maille.niveau, sommerSoc, maille.ouverts, (x) => x.total),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [feuilles, labels, maille.niveau, maille.ouverts]);

  // Chargement de la liste complète dès qu'une seule société est retenue.
  useEffect(() => {
    if (!selUnique) return undefined;
    let actif = true;
    api.debiteurs(selUnique, branche)
      .then((r) => { if (actif) setListeComplete(r.data.debiteurs); })
      .catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Erreur de chargement'); });
    return () => { actif = false; };
  }, [selUnique, branche]);
  const ouvrirDrill = async (d) => {
    if (!selUnique) return;
    setDrill({ tiers: d.tiers, nom: d.nom, echeances: null });
    try { const r = await api.echeances(selUnique, d.tiers, branche); setDrill({ tiers: d.tiers, nom: d.nom, echeances: r.data.echeances }); }
    catch (e) { setDrill({ tiers: d.tiers, nom: d.nom, echeances: [], erreur: e.response?.data?.error || 'Erreur' }); }
  };

  // Le filtre d'ancienneté ne masque aucune colonne : il ne garde que les
  // clients qui doivent quelque chose sur la tranche choisie, et les reclasse
  // sur elle. Les autres colonnes restent lisibles — c'est ce qui permet de
  // voir qu'un client à + 360 j doit aussi sur du récent.
  const lignes = useMemo(() => {
    if (!selUnique) return [];
    // Tant que la liste complète n'est pas là, on montre le top du résumé —
    // l'écran ne reste jamais vide pendant le chargement.
    const base = listeComplete || (data?.debiteurs[selUnique] || []);
    const t = q.trim().toLowerCase();
    let l = t ? base.filter((r) => (r.nom || '').toLowerCase().includes(t) || (r.tiers || '').toLowerCase().includes(t)) : base;
    if (clientsUrl) l = l.filter((r) => (clientsUrl === 'groupe' ? r.groupe : clientsUrl === 'externes' ? !r.groupe : false));
    if (trancheCli >= 0) l = l.filter((r) => r.cells[trancheCli] > 0).slice().sort((a, b) => b.cells[trancheCli] - a.cells[trancheCli]);
    return l;
  }, [selUnique, listeComplete, data, q, trancheCli, clientsUrl]);

  useEffect(() => { setPage(0); }, [q, trancheCli, clientsUrl]);
  const PAR_PAGE = 20;
  const nbPages = Math.max(1, Math.ceil(lignes.length / PAR_PAGE));
  const pageSure = Math.min(page, nbPages - 1);
  const visibles = lignes.slice(pageSure * PAR_PAGE, pageSure * PAR_PAGE + PAR_PAGE);

  const { exportExcel } = useBi(); // droit « Export Excel » (Administration ▸ Accès)
  const exporterCSV = async () => {
    if (!selUnique) return;
    try { await apiBi.post('/bi/exports', { rapport: 'Créances clients', tableau: `Balance âgée — ${courant?.nom || ''}`, lignes: lignes.length, format: 'csv' }); } catch (e) { return; }
    // L'export suit ce qui est AFFICHÉ (recherche et tranche comprises) :
    // exporter autre chose que ce qu'on regarde est un piège.
    const rows = lignes;
    const sep = ';';
    const entete = ['Code tiers', 'Libellé tiers', ...labels, 'Total'].join(sep);
    const corps = rows.map((r) => [`"${(r.tiers || '').replace(/"/g, '""')}"`, `"${(r.nom || '').replace(/"/g, '""')}"`, ...r.cells.map((v) => Math.round(v)), Math.round(r.tot)].join(sep));
    const total = ['TOTAL', '', ...labels.map((_, i) => Math.round(rows.reduce((s, r) => s + r.cells[i], 0))), Math.round(rows.reduce((s, r) => s + r.tot, 0))].join(sep);
    const csv = '﻿' + [entete, ...corps, total].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `creances_${(courant.nom || 'societe').replace(/\s+/g, '_')}_${(data?.arrete || '').slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  const Filtres = (
    <FiltresPanel
      ouvert={panneauOuvert} onToggle={togglePanneau}
      societesDispo={societes} societesActives={selected}
      onToggleSociete={toggleSociete}
      onToutesSocietes={() => setFiltre('')}
      onAucuneSociete={() => setFiltre('__aucune__')}
      categoriesDispo={[{ code: 'externes', nom: 'Hors groupe' }, { code: 'groupe', nom: 'Intra-groupe' }]}
      categoriesActives={clientsActifs} onToggleCategorie={toggleClients} titreCategories="Clients" categoriesApres
      couleurDe={couleurDe} nbActifs={(societesUrl ? 1 : 0) + (clientsUrl ? 1 : 0)}
      onReset={() => { const next = new URLSearchParams(params); next.delete('societes'); next.delete('clients'); setParams(next, { replace: true }); }}
    />
  );

  if (erreur) return <main className="cr-page"><div className="bi-empty"><h2>Rapport indisponible</h2><p>{erreur}</p></div></main>;
  if (!data) return <main className="cr-page"><div className="cr-skel" /></main>;
  if (societes.length === 0) {
    return (<main className="cr-page"><div className="bi-empty"><h2>Aucune société dans votre périmètre</h2><p>Les créances apparaîtront ici pour les sociétés que votre habilitation autorise.</p></div></main>);
  }

  const vide = selected.length === 0;
  const b = courant.buckets, tot = somme(b);
  const nonEchu = b[0], echu = tot - nonEchu, plus90 = b[4] + b[5] + b[6];
  const dateArrete = data.arrete ? new Date(data.arrete).toLocaleDateString('fr-FR') : '—';
  // « Encours hors groupe » : le chiffre d'un coup d'œil, calculé sur les
  // sociétés cochées QUEL QUE SOIT le filtre Clients.
  const ext = selBrut.reduce((a, x) => ({ tot: a.tot + (x.ext?.total || 0), echu: a.echu + (x.ext?.total || 0) - (x.ext?.buckets?.[0] || 0) }), { tot: 0, echu: 0 });
  const grp = selBrut.reduce((a, x) => ({ tot: a.tot + (x.grp?.total || 0), n: a.n + (x.grp?.clients || 0) }), { tot: 0, n: 0 });
  const K = [
    { l: `Encours total${clientsUrl === 'externes' ? ' · hors groupe' : clientsUrl === 'groupe' ? ' · intra-groupe' : ''}`, val: cM(tot), meta: nf.format(courant.clients) + ' clients' },
    { l: 'Encours hors groupe', val: cM(ext.tot), meta: `dont échu ${cM(ext.echu)} · intra-groupe ${cM(grp.tot)} (${nf.format(grp.n)} compte${grp.n > 1 ? 's' : ''})` },
    { l: 'Non échu', val: cM(nonEchu), meta: pct(nonEchu, tot) + " % de l'encours" },
    { l: 'Échu', val: cM(echu), meta: pct(echu, tot) + " % de l'encours" },
    { l: 'À risque · + 90 j', val: cM(plus90), meta: pct(plus90, tot) + " % de l'encours", alert: true },
    { l: 'Tranche + 360 j', val: cM(b[6]), meta: pct(b[6], tot) + " % de l'encours" },
  ];
  const totaux = labels.map((_, i) => lignes.reduce((s, r) => s + r.cells[i], 0));
  const enChargement = !!selUnique && !listeComplete;

  return (
    <>
      <main className="cr-page">
        <div className="cr-head">
          <div>
            <h1>Créances clients — Balance âgée{branche === 'groupe' ? ' · Groupe' : ''}</h1>
            
          </div>
          <div className="cr-asof">Arrêté au <b>{dateArrete}</b><br /><span>source recouvrements · A97 Data</span></div>
        </div>

        {vide ? (
          <div className="bi-empty" style={{ marginTop: 20 }}><h2>Aucune société sélectionnée</h2><p>Cochez au moins une société dans les filtres à droite.</p></div>
        ) : (
          <>
            <div className="cr-kpis">
              {K.map((k, i) => (
                <div className={`cr-kpi${k.alert ? ' alert' : ''}`} key={i}>
                  <div className="cr-kpi-l">{k.l}</div>
                  <div className="cr-kpi-v">{k.val}</div>
                  <div className="cr-kpi-m">{k.meta}</div>
                </div>
              ))}
            </div>

            <div className="cr-card">
              <div className="cr-card-h">
                <h2>Créances par concession</h2>
                <div className="st-tools">
                  {clientsUrl && <span className="hint">{clientsUrl === 'externes' ? 'clients hors groupe seulement' : clientsUrl === 'groupe' ? 'comptes intra-groupe seulement' : 'aucun type de client coché'} · intra-groupe = code tiers « 3 chiffres + nom »</span>}
                  {/* L'unité AU MILIEU, la maille TOUT À DROITE — même ordre que
                      le bloc Stock par concession, pour qu'on retrouve le geste
                      d'un rapport à l'autre. */}
                  <div className="st-seg">
                    <button type="button" className={uniteSoc === 'montants' ? 'on' : ''} onClick={() => setUniteSoc('montants')}>Montants</button>
                    <button type="button" className={uniteSoc === 'clients' ? 'on' : ''} onClick={() => setUniteSoc('clients')}>Nb clients</button>
                    <button type="button" className={uniteSoc === 'part' ? 'on' : ''} onClick={() => setUniteSoc('part')}>%</button>
                  </div>
                  <SelecteurMaille niveau={maille.niveau} setNiveau={maille.setNiveau} dispo={axes} />
                </div>
              </div>
              <div className="cr-scroll">
                <table className="cr-tbl cr-tbl-soc">
                  <thead><tr>
                    <th>{maille.niveau === 'concession' ? 'Société' : 'Regroupement'}</th>
                    <th className="num">{uniteSoc === 'part' ? "Part de l'encours" : 'Total'}</th>
                    {labels.map((l, i) => <th key={i} className="num">{l}</th>)}
                  </tr></thead>
                  <tbody>
                    {arbre.map((x) => {
                      // En % : chaque ligne se répartit sur ses tranches (elle
                      // fait 100 %), et la colonne de gauche dit sa part de
                      // l'encours total. Deux lectures, jamais la même deux fois.
                      const cel = uniteSoc === 'clients' ? (x.clientsBuckets || []) : x.buckets;
                      const base = uniteSoc === 'clients'
                        ? (x.clientsBuckets || []).reduce((a, v) => a + v, 0) : x.total;
                      // Une ligne de regroupement filtre sur TOUS ses membres ;
                      // le dépliage a son propre bouton (jamais la même cible).
                      const cible = x.feuille ? x.code : (x.membres || []).join(',');
                      return (
                        <tr key={x.cle} className={`tbl-clic rg-n${x.niveau}${societesUrl === cible ? ' actif' : ''}`}
                            onClick={() => setFiltre(basculer(societesUrl, cible))}>
                          <td>
                            <span className="rg-in" style={{ width: x.niveau * 14 }} />
                            {x.feuille ? <span className="rg-chev vide" /> : (
                              <button type="button" className={`rg-btn${maille.ouverts.has(x.cle) ? ' on' : ''}`}
                                title={maille.ouverts.has(x.cle) ? 'Replier' : 'Voir le détail par concession'}
                                aria-label="Déplier"
                                onClick={(e) => { e.stopPropagation(); maille.basculer(x.cle); }}>▶</button>
                            )}
                            {x.libelle || x.nom}
                            {!x.feuille && <span className="rg-nb">{x.nbEnfants}</span>}
                          </td>
                          <td className="num strong">
                            {uniteSoc === 'clients' ? nf.format(x.clients)
                              : uniteSoc === 'part' ? pc1(x.total, tot) : cM(x.total)}
                          </td>
                          {labels.map((_, i) => {
                            const v = cel[i] || 0;
                            return (
                              <td key={i} className={`num${v ? '' : ' muted'}`}>
                                {uniteSoc === 'part' ? pc1(v, base)
                                  : v ? (uniteSoc === 'clients' ? nf.format(v) : kc(v)) : '·'}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                  {sel.length > 1 && (
                    <tfoot><tr>
                      <td>Total</td>
                      <td className="num strong">
                        {uniteSoc === 'clients' ? nf.format(courant.clients)
                          : uniteSoc === 'part' ? '100 %' : cM(tot)}
                      </td>
                      {labels.map((_, i) => {
                        const v = uniteSoc === 'clients' ? (courant.clientsBuckets || [])[i] || 0 : b[i];
                        const baseT = uniteSoc === 'clients'
                          ? (courant.clientsBuckets || []).reduce((a, z) => a + z, 0) : tot;
                        return (
                          <td key={i} className="num">
                            {uniteSoc === 'part' ? pc1(v, baseT)
                              : v ? (uniteSoc === 'clients' ? nf.format(v) : kc(v)) : '·'}
                          </td>
                        );
                      })}
                    </tr></tfoot>
                  )}
                </table>
              </div>
            </div>

            <div className="cr-card">
              <div className="cr-card-h">
                <h2>Balance âgée par client</h2>
                <div className="st-tools">
                  <span className="hint">
                    {!selUnique ? 'sélectionnez une seule société pour le détail par client'
                      : enChargement ? 'chargement de la liste complète…' : ''}
                  </span>
                  {selUnique && (
                    <>
                      <div className="st-seg">
                        <button type="button" className={trancheCli === -1 ? 'on' : ''} onClick={() => setTrancheCli(-1)}>Toutes</button>
                        {labels.map((l, i) => (
                          <button key={i} type="button" className={trancheCli === i ? 'on' : ''} onClick={() => setTrancheCli(trancheCli === i ? -1 : i)}>{l}</button>
                        ))}
                      </div>
                      {/* L'unité TOUT À DROITE : le k€ pour comparer des lignes,
                          l'euro pour rapprocher une facture. */}
                      <div className="st-seg">
                        <button type="button" className={uniteCli === 'k' ? 'on' : ''} onClick={() => setUniteCli('k')}>k€</button>
                        <button type="button" className={uniteCli === 'e' ? 'on' : ''} onClick={() => setUniteCli('e')}>€</button>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {!selUnique ? (
                <div className="cr-card-b"><p className="cr-empty-tbl">Vue consolidée ci-dessus. Cochez <b>une seule</b> société (dans les filtres) pour dérouler ses clients, les parcourir, les exporter et ouvrir leurs échéances.</p></div>
              ) : (
                <>
                  <div className="cr-tools">
                    <input className="cr-search" placeholder="🔍 Filtrer un client (code ou libellé)…" value={q} onChange={(e) => setQ(e.target.value)} />
                    {exportExcel && <button className="cr-btn ghost" onClick={exporterCSV}>⬇ Exporter (CSV)</button>}
                    <span className="cr-tools-note">l'export reprend les {nf.format(lignes.length)} lignes retenues, pas la seule page affichée</span>
                  </div>
                  <div className="cr-scroll">
                    <table className="cr-tbl">
                      <thead>
                        <tr>
                          <th className="cd">Code tiers</th>
                          <th className="cli">Libellé tiers</th>
                          {labels.map((l, i) => <th key={i} className={trancheCli === i ? 'cr-th-on' : ''}>{l}</th>)}
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibles.map((r) => (
                          <tr key={r.tiers} className="tbl-clic" onClick={() => ouvrirDrill(r)}>
                            <td className="cd">{r.tiers}</td>
                            <td className="cli"><span className="rs">{r.nom}</span>{r.groupe && <span className="cr-grp" title="Compte d'une société du groupe (code tiers « 3 chiffres + nom »)">groupe</span>}</td>
                            {r.cells.map((v, i) => (
                              <td key={i} className={`num${v ? '' : ' vide'}${trancheCli === i ? ' cr-td-on' : ''}`}
                                title={v ? `${labels[i]} : ${eur(v)}` : undefined}>{mont(v, uniteCli)}</td>
                            ))}
                            <td className="tot">{uniteCli === 'e' ? eur(r.tot) : cM(r.tot)}</td>
                          </tr>
                        ))}
                        {lignes.length === 0 && (
                          <tr><td className="cli" colSpan={labels.length + 3} style={{ textAlign: 'center', color: 'var(--faint,#96a0b0)', padding: 22 }}>
                            {enChargement ? 'Chargement…' : 'Aucun client ne correspond.'}
                          </td></tr>
                        )}
                      </tbody>
                      <tfoot>
                        <tr className="total">
                          <td className="cd" />
                          <td className="cli">TOTAL{trancheCli >= 0 ? ` · ${labels[trancheCli]}` : ''}{q ? ' · recherche' : ''}</td>
                          {totaux.map((v, i) => <td key={i} className={`num${trancheCli === i ? ' cr-td-on' : ''}`}>{mont(v, uniteCli)}</td>)}
                          <td className="tot">{uniteCli === 'e' ? eur(somme(totaux)) : cM(somme(totaux))}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  {nbPages > 1 && (
                    <div className="cr-pages">
                      <button type="button" className="cr-btn ghost" disabled={pageSure === 0}
                        onClick={() => setPage(0)} aria-label="Première page">«</button>
                      <button type="button" className="cr-btn ghost" disabled={pageSure === 0}
                        onClick={() => setPage(pageSure - 1)}>‹ Précédents</button>
                      <span className="cr-pages-n">
                        {nf.format(pageSure * PAR_PAGE + 1)}–{nf.format(Math.min((pageSure + 1) * PAR_PAGE, lignes.length))}
                        {' '}sur {nf.format(lignes.length)}
                        <small>page {pageSure + 1} / {nbPages}</small>
                      </span>
                      <button type="button" className="cr-btn ghost" disabled={pageSure >= nbPages - 1}
                        onClick={() => setPage(pageSure + 1)}>Suivants ›</button>
                      <button type="button" className="cr-btn ghost" disabled={pageSure >= nbPages - 1}
                        onClick={() => setPage(nbPages - 1)} aria-label="Dernière page">»</button>
                    </div>
                  )}
                </>
              )}
            </div>

            <p className="cr-foot">
              Balance âgée calculée par Datacar, arrêtée au {dateArrete}. Sept tranches d'affichage ; la source détaille jusqu'à + 720 jours.
              Les tiers de financement internes (Somafi, Soguafi, Crédit Moderne…) sont inclus dans l'encours.
            </p>
          </>
        )}
      </main>

      {drill && (
        <div className="cr-modal-ov" onClick={() => setDrill(null)}>
          <div className="cr-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cr-modal-h">
              <b>{drill.nom}</b>
              <span>{drill.echeances ? `${drill.echeances.length} échéance(s) · ${eur(drill.echeances.reduce((s, e) => s + e.montant, 0))}` : ''}</span>
              <button className="cr-modal-x" onClick={() => setDrill(null)}>✕</button>
            </div>
            <div className="cr-modal-b">
              {!drill.echeances ? <div className="cr-empty-tbl">Chargement…</div> : drill.echeances.length === 0 ? (
                <div className="cr-empty-tbl">{drill.erreur || 'Aucune échéance.'}</div>
              ) : (
                <table className="cr-tbl cr-tbl-detail">
                  <thead>
                    <tr><th>Échéance</th><th>Retard</th><th>Tranche</th><th>Référence</th><th>Libellé</th><th>Mode</th><th>Relevé</th><th className="num">Montant</th></tr>
                  </thead>
                  <tbody>
                    {drill.echeances.map((e, i) => (
                      <tr key={i}>
                        <td>{e.echeance ? e.echeance.split('-').reverse().join('/') : '—'}</td>
                        <td className="num">{e.jours != null ? `${e.jours} j` : '—'}</td>
                        <td>{e.tranche || '—'}</td>
                        <td className="cd">{e.reference || '—'}</td>
                        <td>{e.libelle || '—'}</td>
                        <td>{e.mode || '—'}</td>
                        <td className="cd">{e.releve || '—'}</td>
                        <td className="num"><b>{eur(e.montant)}</b></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {Filtres}
    </>
  );
}
